import { useEffect, useRef } from "react";
import { env } from "@/shared/config/env";
import { mintEditorToken, type MintErrorCode } from "../api/mintEditorToken";

interface AuthRequest {
  type: "TEMPLATE_EDITOR_AUTH_REQUEST";
  requestId: string;
}

interface AuthTokenMessage {
  type: "TEMPLATE_EDITOR_AUTH_TOKEN";
  requestId: string;
  editorToken: string;
  expiresAt: string;
  expiresInSec: number;
}

interface AuthErrorMessage {
  type: "TEMPLATE_EDITOR_AUTH_ERROR";
  requestId: string;
  code: MintErrorCode;
  message?: string;
}

function isAuthRequest(data: unknown): data is AuthRequest {
  return (
    typeof data === "object" &&
    data !== null &&
    (data as { type?: unknown }).type === "TEMPLATE_EDITOR_AUTH_REQUEST" &&
    typeof (data as { requestId?: unknown }).requestId === "string"
  );
}

/**
 * How long the host may take to answer the Studio's token request.
 *
 * Comfortably inside the Studio's own 8s give-up (REQUEST_TIMEOUT_MS in its
 * request-token-from-host.ts), so a slow host produces OUR error — which names
 * a cause and offers a retry — rather than its generic "Timed out waiting for
 * host to respond with an editor token".
 */
export const REPLY_DEADLINE_MS = 6000;

/**
 * Answers the Studio iframe's token handshake.
 *
 * The origin check is the load-bearing line: any script on the page can
 * postMessage into this window, so trusting the iframe's `src` is not enough.
 * Nothing about `event.data` is read until the origin matches.
 *
 * `getAuth0Token` is injected so this hook is testable without an Auth0
 * provider; the frame component passes `getAccessTokenSilently`.
 */
export function useEditorAuthBridge(
  iframeRef: React.RefObject<HTMLIFrameElement | null>,
  storeId: string | undefined,
  getAuth0Token: () => Promise<string>,
): void {
  // Keep the latest getter without re-installing the listener every render.
  const getTokenRef = useRef(getAuth0Token);
  getTokenRef.current = getAuth0Token;

  useEffect(() => {
    const studioOrigin = env.STUDIO_ORIGIN;
    if (!studioOrigin) return;

    const inFlight = new Set<string>();

    const reply = (message: AuthTokenMessage | AuthErrorMessage) => {
      const win = iframeRef.current?.contentWindow;
      if (!win) return;
      // Explicit target origin, never "*" — a wildcard would hand the token to
      // whatever page happens to occupy the frame.
      win.postMessage(message, studioOrigin);
    };

    const handle = async (requestId: string) => {
      if (inFlight.has(requestId)) return;
      inFlight.add(requestId);

      // Always answer, and answer in time.
      //
      // The Studio gives up after 8s and shows "Timed out waiting for host to
      // respond with an editor token" — a message that tells the merchant
      // nothing and, worse, means the host looks broken when the host is
      // merely slow. Neither of the awaits below has a timeout of its own:
      // getAccessTokenSilently can stall on a blocked silent-auth iframe
      // (third-party cookies) and fetch can hang on a dead connection rather
      // than erroring. Both were paths where we simply never replied.
      //
      // So: a deadline inside the Studio's, and a `settled` latch so the
      // deadline and the real result cannot both answer.
      let settled = false;
      const answer = (message: AuthTokenMessage | AuthErrorMessage) => {
        if (settled) return;
        settled = true;
        reply(message);
      };
      const deadline = window.setTimeout(() => {
        answer({
          type: "TEMPLATE_EDITOR_AUTH_ERROR",
          requestId,
          code: "UNAVAILABLE",
          message: "Timed out preparing the editor session.",
        });
      }, REPLY_DEADLINE_MS);

      try {
        if (!storeId) {
          answer({
            type: "TEMPLATE_EDITOR_AUTH_ERROR",
            requestId,
            code: "UNAVAILABLE",
            message: "No active store to scope the editor session to.",
          });
          return;
        }

        let auth0Token: string;
        try {
          auth0Token = await getTokenRef.current();
        } catch {
          answer({
            type: "TEMPLATE_EDITOR_AUTH_ERROR",
            requestId,
            code: "UNAUTHORIZED",
            message: "Dashboard session expired; please sign in again.",
          });
          return;
        }

        const result = await mintEditorToken(auth0Token, storeId);
        if (!result.ok) {
          answer({
            type: "TEMPLATE_EDITOR_AUTH_ERROR",
            requestId,
            code: result.code,
            message: result.message,
          });
          return;
        }

        answer({
          type: "TEMPLATE_EDITOR_AUTH_TOKEN",
          requestId,
          editorToken: result.data.editorToken,
          expiresAt: result.data.expiresAt,
          expiresInSec: result.data.expiresInSec,
        });
      } finally {
        window.clearTimeout(deadline);
        inFlight.delete(requestId);
      }
    };

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== studioOrigin) return; // must come first
      if (!isAuthRequest(event.data)) return;
      void handle(event.data.requestId);
    };

    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      inFlight.clear();
    };
    // storeId matters: if the merchant switches store while the frame stays
    // mounted, later mints must scope to the new store.
  }, [iframeRef, storeId]);
}
